import { useState } from 'react';
import { 
  Search,
  Building,
  Users,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ArrowUpRight,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Agent, DepartmentMetrics } from '@/types/supervisor';

const departmentMetrics: DepartmentMetrics[] = [];
const agents: Agent[] = [];
import { cn } from '@/lib/utils';

interface DepartmentViewProps {
  onDepartmentSelect?: (department: DepartmentMetrics) => void;
}

export function DepartmentView({ onDepartmentSelect }: DepartmentViewProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDept, setSelectedDept] = useState<DepartmentMetrics | null>(null);

  const filteredDepartments = departmentMetrics.filter(dept =>
    dept.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalOpenTickets = departmentMetrics.reduce((acc, d) => acc + d.openTickets, 0);
  const totalPendingTickets = departmentMetrics.reduce((acc, d) => acc + d.pendingTickets, 0);
  const totalAgentsOnline = departmentMetrics.reduce((acc, d) => acc + d.agentsOnline, 0);
  const totalAgents = departmentMetrics.reduce((acc, d) => acc + d.totalAgents, 0);

  const handleDeptClick = (dept: DepartmentMetrics) => {
    setSelectedDept(dept);
    onDepartmentSelect?.(dept);
  };

  const deptAgents = selectedDept 
    ? agents.filter(a => a.department === selectedDept.name)
    : [];

  return (
    <div className="flex-1 overflow-auto animate-fade-in">
      <div className="flex flex-col h-full sm:flex-row">
        {/* Department List */}
        <div className={cn(
          "border-b-2 overflow-auto transition-all sm:border-b-0 sm:border-r-2",
          selectedDept ? "sm:w-1/2" : "w-full p-4 sm:p-6"
        )}>
          {!selectedDept && (
            <>
              {/* Header */}
              <div className="flex flex-wrap items-start justify-between gap-3 mb-4 sm:mb-6">
                <div className="space-y-0.5 sm:space-y-1">
                  <h1 className="text-xl font-bold flex items-center gap-2 sm:text-2xl">
                    <Building className="h-5 w-5 text-primary sm:h-6 sm:w-6" />
                    Departments
                  </h1>
                  <p className="text-sm text-muted-foreground sm:text-base">Cross-department visibility and management</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-xs">
                    {totalAgentsOnline}/{totalAgents} agents online
                  </Badge>
                  <Badge variant="outline" className="text-xs border-warning text-warning">
                    {totalOpenTickets} open tickets
                  </Badge>
                </div>
              </div>

              {/* Search */}
              <div className="relative mb-4 sm:mb-6">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search departments..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
            </>
          )}

          {selectedDept && (
            <div className="p-4 border-b-2">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedDept(null)}
                className="mb-2"
              >
                ← All Departments
              </Button>
            </div>
          )}

          {/* Department Cards */}
          <div className={cn(
            "space-y-4",
            selectedDept && "p-4"
          )}>
            {(selectedDept ? [selectedDept] : filteredDepartments).map((dept) => {
              const isSelected = selectedDept?.id === dept.id;
              
              return (
                <Card 
                  key={dept.id}
                  className={cn(
                    "border-2 cursor-pointer transition-all hover:border-primary/50",
                    isSelected && "border-primary"
                  )}
                  onClick={() => !isSelected && handleDeptClick(dept)}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-lg">{dept.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex items-center gap-1">
                            <div className="w-2 h-2 rounded-full bg-success" />
                            <span className="text-xs text-muted-foreground">
                              {dept.agentsOnline}/{dept.totalAgents} online
                            </span>
                          </div>
                        </div>
                      </div>
                      {dept.escalatedTickets > 0 && (
                        <Badge variant="destructive" className="text-xs">
                          <AlertTriangle className="h-3 w-3 mr-1" />
                          {dept.escalatedTickets} escalated
                        </Badge>
                      )}
                    </div>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div className="space-y-1">
                        <p className="text-2xl font-bold text-primary">{dept.openTickets}</p>
                        <p className="text-xs text-muted-foreground">Open Tickets</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-2xl font-bold text-warning">{dept.pendingTickets}</p>
                        <p className="text-xs text-muted-foreground">Pending</p>
                      </div>
                    </div>

                    {/* SLA Progress */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">SLA Compliance</span>
                        <span className={cn(
                          "font-semibold",
                          dept.slaCompliance >= 90 ? "text-success" :
                          dept.slaCompliance >= 80 ? "text-warning" : "text-destructive"
                        )}>
                          {dept.slaCompliance}%
                        </span>
                      </div>
                      <Progress 
                        value={dept.slaCompliance} 
                        className={cn(
                          "h-2",
                          dept.slaCompliance >= 90 && "[&>div]:bg-success",
                          dept.slaCompliance >= 80 && dept.slaCompliance < 90 && "[&>div]:bg-warning",
                          dept.slaCompliance < 80 && "[&>div]:bg-destructive"
                        )}
                      />
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between mt-4 pt-4 border-t text-xs text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        <span>Avg Resolution: {dept.avgResolutionTime}</span>
                      </div>
                      {!isSelected && (
                        <Button variant="ghost" size="sm" className="h-6 text-xs">
                          View Details
                          <ArrowUpRight className="h-3 w-3 ml-1" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Department Detail Panel */}
        {selectedDept && (
          <div className="w-full overflow-auto p-4 space-y-4 bg-muted/20 sm:w-1/2 sm:p-6 sm:space-y-6">
            <div className="space-y-1">
              <h2 className="text-xl font-bold">{selectedDept.name} Team</h2>
              <p className="text-muted-foreground text-sm">
                {deptAgents.length} agents • {selectedDept.openTickets + selectedDept.pendingTickets} active tickets
              </p>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="text-xs">
                Reassign Tickets
              </Button>
              <Button size="sm" variant="outline" className="text-xs">
                Transfer Tickets
              </Button>
              <Button size="sm" variant="outline" className="text-xs">
                View All Tickets
              </Button>
            </div>

            {/* Team Members */}
            <Card className="border-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Team Members
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {deptAgents.length > 0 ? deptAgents.map((agent) => {
                  const loadPercentage = (agent.currentLoad / agent.maxCapacity) * 100;
                  
                  return (
                    <div 
                      key={agent.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-background hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-semibold text-primary">
                              {agent.name.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div className={cn(
                            "absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-card",
                            agent.status === 'online' && "bg-success",
                            agent.status === 'busy' && "bg-warning",
                            agent.status === 'away' && "bg-muted-foreground",
                            agent.status === 'offline' && "bg-muted"
                          )} />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{agent.name}</p>
                          <p className="text-xs text-muted-foreground capitalize">{agent.status}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={cn(
                          "text-sm font-medium",
                          loadPercentage >= 90 ? "text-destructive" :
                          loadPercentage >= 70 ? "text-warning" : "text-success"
                        )}>
                          {agent.currentLoad}/{agent.maxCapacity}
                        </p>
                        <p className="text-xs text-muted-foreground">{agent.slaCompliance}% SLA</p>
                      </div>
                    </div>
                  );
                }) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No agents found in this department
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Department Stats */}
            <Card className="border-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Performance Metrics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-background space-y-1">
                    <p className="text-xs text-muted-foreground">Avg Resolution Time</p>
                    <p className="text-lg font-bold">{selectedDept.avgResolutionTime}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background space-y-1">
                    <p className="text-xs text-muted-foreground">SLA Compliance</p>
                    <p className={cn(
                      "text-lg font-bold",
                      selectedDept.slaCompliance >= 90 ? "text-success" : 
                      selectedDept.slaCompliance >= 80 ? "text-warning" : "text-destructive"
                    )}>
                      {selectedDept.slaCompliance}%
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-background space-y-1">
                    <p className="text-xs text-muted-foreground">Open Tickets</p>
                    <p className="text-lg font-bold text-primary">{selectedDept.openTickets}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-background space-y-1">
                    <p className="text-xs text-muted-foreground">Escalated</p>
                    <p className={cn(
                      "text-lg font-bold",
                      selectedDept.escalatedTickets > 0 ? "text-destructive" : "text-success"
                    )}>
                      {selectedDept.escalatedTickets}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
